import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CouponUsagePanel } from '../components/pricing/CouponUsagePanel';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, DollarSign, Tag, Stethoscope, Crown, Receipt, Percent, BookOpen, Archive, Sparkles, Baby, Link2, Copy } from 'lucide-react';
import { PageHeader, StatusPill } from '../components/ds';
import { getApiUrl } from '../utils/api';

// Same-origin on production (ops.soulsanskar.com) to avoid stale-build CORS.
const API_URL = getApiUrl();

// Razorpay everywhere — PayPal retired 19 Jun 2026 (Razorpay International is live).
const COUNTRY_PRESETS = {
  India: { currency: 'INR', symbol: '₹', payment_gateway: 'razorpay' },
  'United States': { currency: 'USD', symbol: '$', payment_gateway: 'razorpay' },
  'United Kingdom': { currency: 'GBP', symbol: '£', payment_gateway: 'razorpay' },
  Singapore: { currency: 'SGD', symbol: 'S$', payment_gateway: 'razorpay' },
  Australia: { currency: 'AUD', symbol: 'A$', payment_gateway: 'razorpay' },
  Canada: { currency: 'CAD', symbol: 'C$', payment_gateway: 'razorpay' },
  'United Arab Emirates': { currency: 'AED', symbol: 'د.إ', payment_gateway: 'razorpay' },
};

const CURRENCY_SYMBOL = { INR: '₹', USD: '$', GBP: '£', SGD: 'S$', AUD: 'A$', AED: 'د.إ', EUR: '€', CAD: 'C$' };

const DEFAULT_SLABS = [
  { min_months: 1, max_months: 2, discount_pct: 0 },
  { min_months: 3, max_months: 4, discount_pct: 10 },
  { min_months: 5, max_months: 6, discount_pct: 15 },
  { min_months: 7, max_months: null, discount_pct: 20 },
];

const blank = {
  soulai_coach_monthly_price: 899, soulai_coach_monthly_strikeoff: 1299, soulai_till_delivery_slabs: DEFAULT_SLABS,
  soulai_till_delivery_discount_pct: 20, soulai_till_delivery_effective_price: 0, soulai_till_delivery_display_as: 'percent',
  baby_tracker_monthly_price: 199, baby_tracker_monthly_strikeoff: 299,
  baby_tracker_yearly_price: 1499, baby_tracker_yearly_strikeoff: 2388,
  country: 'India', currency: 'INR', payment_gateway: 'razorpay',
  trial_price: 99, trial_strikeoff_price: 499,
  garbhsanskar_addon_price: 199, garbhsanskar_addon_strikeoff: 499,
  garbhsanskar_full_price: 1499, garbhsanskar_full_strikeoff: 2999,
  gynae_consultation_price: 99, gynae_consultation_strikeoff: 499,
  nutritionist_consultation_price: 199, nutritionist_consultation_strikeoff: 499,
  elite_monthly_price: 2999, elite_monthly_strikeoff: 4999,
  default_monthly_price: 2999, till_delivery_starting_price: 2249,
  gst_percentage: 18, prices_include_gst: true, active_status: true,
};

const PricingPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editTab, setEditTab] = useState('trial');
  const [region, setRegion] = useState('all');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/pricing`, { withCredentials: true });
      setRows(res.data || []);
    } catch (e) {
      toast.error('Failed to load pricing');
    } finally { setLoading(false); }
  };

  const handleEdit = (row, tab = 'trial') => { setEditing(row); setEditTab(tab); setShowModal(true); };
  const handleAdd = () => { setEditing(null); setEditTab('trial'); setShowModal(true); };

  const handleDelete = async (row) => {
    if (row.is_seeded) {
      toast.error('Seeded rows (India / US) cannot be deleted. Toggle active status instead.');
      return;
    }
    if (!confirm(`Delete pricing for ${row.country}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/pricing/${row.pricing_id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const visibleRows = region === 'all' ? rows : rows.filter((r) => (region === 'INR' ? r.currency === 'INR' : r.currency !== 'INR'));
  const publicUrl = (country) => `${API_URL}/api/pricing/public/full?country=${encodeURIComponent(country)}`;

  return (
    <div className="space-y-6" data-testid="pricing-page">
      <PageHeader
        title="Pricing & Products"
        description="Single source of truth for Trial, Consultations, Elite, SoulAI Coach and Baby Tracker. Read live by the website, the APP and LMS."
        actions={(
          <button onClick={handleAdd} className="btn-primary flex items-center gap-2" data-testid="add-pricing-btn">
            <Plus className="w-4 h-4" /> Add Country
          </button>
        )}
      />

      <div className="card p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="pricing-lms-sync-banner">
        <div className="flex items-center gap-2 text-sm">
          <Link2 className="w-4 h-4 text-secondary" />
          <span className="font-semibold">LMS / APP feed</span>
          <span className="text-muted-foreground">— every save bumps the version; LMS refreshes within 10 minutes.</span>
        </div>
        <div className="flex flex-wrap gap-2 md:ml-auto">
          {rows.map((r) => (
            <button key={r.pricing_id} type="button" className="btn-secondary text-xs py-1 inline-flex items-center gap-1.5"
              onClick={() => { navigator.clipboard?.writeText(publicUrl(r.country)); toast.success('Public pricing URL copied'); }}
              data-testid={`copy-public-url-${r.currency}`}>
              <Copy className="w-3 h-3" /> {r.currency} feed · v{r.pricing_version || 1}
            </button>
          ))}
        </div>
      </div>

      <div className="inline-flex items-center gap-0.5 rounded-lg bg-muted p-1" role="tablist" data-testid="pricing-region-tabs">
        {[{ key: 'all', label: 'All regions' }, { key: 'INR', label: 'India · INR' }, { key: 'INTL', label: 'International · USD' }].map((o) => (
          <button key={o.key} type="button" role="tab" aria-selected={region === o.key} onClick={() => setRegion(o.key)}
            data-testid={`pricing-region-${o.key}`}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${region === o.key ? 'bg-card text-primary shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}>
            {o.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 stagger">
          {visibleRows.map((row) => (
            <PricingCard
              key={row.pricing_id}
              row={row}
              onEdit={(tab) => handleEdit(row, tab)}
              onDelete={() => handleDelete(row)}
            />
          ))}
        </div>
      )}

      {showModal && (
        <PricingModal
          initialTab={editTab}
          existing={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}

      {/* V29 — Course Catalog (per-course product table) */}
      <CatalogSection />

      {/* V63 — Coupon usage across App / Website / Sales links */}
      <CouponUsagePanel />
    </div>
  );
};

const PricingCard = ({ row, onEdit, onDelete }) => {
  const sym = CURRENCY_SYMBOL[row.currency] || row.currency;
  const Stat = ({ label, price, strike }) => (
    <div className="flex justify-between text-sm py-1 border-b border-slate-100 last:border-0">
      <span className="text-slate-600">{label}</span>
      <span className="text-slate-900 font-medium">
        {sym}{Number(price || 0).toLocaleString()}
        {strike > 0 && (
          <span className="ml-2 text-slate-400 line-through font-normal">
            {sym}{Number(strike).toLocaleString()}
          </span>
        )}
      </span>
    </div>
  );

  return (
    <div className="card p-5 hover-lift" data-testid={`pricing-card-${row.country.replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-slate-900">{row.country}</h3>
            {row.is_seeded && (
              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">SEEDED</span>
            )}
            {!row.active_status && (
              <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-medium">INACTIVE</span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            {row.currency} • Gateway: <span className="font-medium uppercase">{row.payment_gateway}</span>
            {' • GST '}{row.gst_percentage}%{row.prices_include_gst ? ' (incl.)' : ''}
          </p>
        </div>
        <div className="flex gap-1">
          <button onClick={() => onEdit('trial')} className="p-1.5 text-slate-600 hover:bg-slate-100 rounded" data-testid={`edit-${row.pricing_id}`}>
            <Edit2 className="w-4 h-4" />
          </button>
          {!row.is_seeded && (
            <button onClick={onDelete} className="p-1.5 text-red-600 hover:bg-red-50 rounded" data-testid={`delete-${row.pricing_id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Tag className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Trial Class</span>
          </div>
          <Stat label="Trial Price" price={row.trial_price} strike={row.trial_strikeoff_price} />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Stethoscope className="w-3.5 h-3.5 text-indigo-600" />
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Consultations</span>
          </div>
          <Stat label="Gynae" price={row.gynae_consultation_price} strike={row.gynae_consultation_strikeoff} />
          <Stat label="Nutritionist" price={row.nutritionist_consultation_price} strike={row.nutritionist_consultation_strikeoff} />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Crown className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Elite (in-app) + Website Plans</span>
          </div>
          <Stat label="Elite Monthly (in-app)" price={row.elite_monthly_price} strike={row.elite_monthly_strikeoff} />
          <Stat label="Website — Monthly" price={row.default_monthly_price} strike={0} />
          <Stat label="Website — Till Delivery / mo" price={row.till_delivery_starting_price} strike={0} />
        </div>

        <div data-testid={`pricing-soulai-${row.currency}`}>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">SoulAI Coach</span>
              <StatusPill status="active" label="APP + links" className="!py-0 text-[10px]" />
            </div>
            <button onClick={() => onEdit('soulai')} className="text-xs text-primary hover:underline" data-testid={`edit-soulai-${row.pricing_id}`}>Edit</button>
          </div>
          <Stat label="Monthly (30 days, one-time)" price={row.soulai_coach_monthly_price} strike={row.soulai_coach_monthly_strikeoff} />
          <div className="flex flex-wrap gap-1.5 pt-1.5" data-testid={`soulai-td-summary-${row.currency}`}>
            {(() => {
              const monthly = Number(row.soulai_coach_monthly_price) || 0;
              const mode = row.soulai_till_delivery_display_as || 'percent';
              const disc = Number(row.soulai_till_delivery_discount_pct) || 0;
              const price = Number(row.soulai_till_delivery_effective_price) || 0;
              const eff = mode === 'price' ? price : Math.round(monthly * (1 - disc / 100));
              const effDisc = monthly ? Math.round((1 - eff / monthly) * 100) : 0;
              const sym = CURRENCY_SYMBOL[row.currency] || row.currency;
              return (
                <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] text-slate-600 font-mono">
                  Till-Delivery: {mode === 'price' ? `from ${sym}${eff.toLocaleString()}/mo` : `up to ${disc}% off`} ({effDisc}% off)
                </span>
              );
            })()}
          </div>
        </div>

        <div data-testid={`pricing-baby-${row.currency}`}>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <Baby className="w-3.5 h-3.5 text-secondary" />
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Baby Tracker</span>
              <StatusPill status="active" label="APP + links" className="!py-0 text-[10px]" />
            </div>
            <button onClick={() => onEdit('baby')} className="text-xs text-primary hover:underline" data-testid={`edit-baby-${row.pricing_id}`}>Edit</button>
          </div>
          <Stat label="Monthly" price={row.baby_tracker_monthly_price} strike={row.baby_tracker_monthly_strikeoff} />
          <Stat label="Yearly" price={row.baby_tracker_yearly_price} strike={row.baby_tracker_yearly_strikeoff} />
        </div>
      </div>
    </div>
  );
};

const PricingModal = ({ existing, onClose, onSaved, initialTab = 'trial' }) => {
  const [form, setForm] = useState(existing ? { ...blank, ...existing } : blank);
  const [tab, setTab] = useState(initialTab);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  // When country changes (only for NEW rows), preset currency + gateway
  const onCountryChange = (country) => {
    if (existing?.is_seeded) return; // locked for seeded rows
    const preset = COUNTRY_PRESETS[country];
    if (preset) {
      setForm({ ...form, country, currency: preset.currency, payment_gateway: preset.payment_gateway });
    } else {
      set('country', country);
    }
  };

  const validate = () => {
    const pairs = [
      ['Trial', form.trial_price, form.trial_strikeoff_price],
      ['Gynae consultation', form.gynae_consultation_price, form.gynae_consultation_strikeoff],
      ['Nutritionist consultation', form.nutritionist_consultation_price, form.nutritionist_consultation_strikeoff],
      ['Elite monthly', form.elite_monthly_price, form.elite_monthly_strikeoff],
    ];
    for (const [label, p, s] of pairs) {
      if (Number(p) < 0) return `${label} price cannot be negative`;
      if (Number(s) > 0 && Number(s) < Number(p)) {
        return `${label} strikeoff (${s}) must be ≥ price (${p}). Set strikeoff to 0 to hide it.`;
      }
    }
    if (Number(form.till_delivery_starting_price) < 0) return 'Till delivery starting price cannot be negative';
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }

    setSaving(true);
    try {
      // Coerce numeric fields
      const payload = { ...form };
      for (const k of [
        'trial_price', 'trial_strikeoff_price',
        'garbhsanskar_addon_price', 'garbhsanskar_addon_strikeoff',
        'garbhsanskar_full_price', 'garbhsanskar_full_strikeoff',
        'gynae_consultation_price', 'gynae_consultation_strikeoff',
        'nutritionist_consultation_price', 'nutritionist_consultation_strikeoff',
        'elite_monthly_price', 'elite_monthly_strikeoff',
        'default_monthly_price', 'till_delivery_starting_price', 'gst_percentage',
        'soulai_coach_monthly_price', 'soulai_coach_monthly_strikeoff',
        'soulai_till_delivery_discount_pct', 'soulai_till_delivery_effective_price',
      ]) {
        payload[k] = Number(payload[k]) || 0;
      }
      if (existing) {
        await axios.put(`${API_URL}/api/pricing/${existing.pricing_id}`, payload, { withCredentials: true });
        toast.success('Pricing updated');
      } else {
        await axios.post(`${API_URL}/api/pricing`, payload, { withCredentials: true });
        toast.success('Pricing created');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const sym = CURRENCY_SYMBOL[form.currency] || form.currency;

  const Field = ({ label, value, onChange, type = 'number', helper, ...rest }) => (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="input-base"
        {...rest}
      />
      {helper && <p className="text-[11px] text-slate-500 mt-0.5">{helper}</p>}
    </div>
  );

  const PriceRow = ({ keyPrefix, label }) => (
    <div className="grid grid-cols-2 gap-3">
      <Field
        label={`${label} — Price (${sym})`}
        value={form[`${keyPrefix}_price`] ?? form[`${keyPrefix}_consultation_price`] ?? form[`${keyPrefix}_monthly_price`]}
        onChange={(v) => set(
          form[`${keyPrefix}_price`] !== undefined
            ? `${keyPrefix}_price`
            : form[`${keyPrefix}_consultation_price`] !== undefined
              ? `${keyPrefix}_consultation_price`
              : `${keyPrefix}_monthly_price`,
          v
        )}
        data-testid={`field-${keyPrefix}-price`}
      />
      <Field
        label={`${label} — MRP / Strikeoff (${sym})`}
        value={
          form[`${keyPrefix}_strikeoff_price`]
          ?? form[`${keyPrefix}_strikeoff`]
          ?? form[`${keyPrefix}_consultation_strikeoff`]
          ?? form[`${keyPrefix}_monthly_strikeoff`]
        }
        onChange={(v) => set(
          form[`${keyPrefix}_strikeoff_price`] !== undefined
            ? `${keyPrefix}_strikeoff_price`
            : form[`${keyPrefix}_strikeoff`] !== undefined
              ? `${keyPrefix}_strikeoff`
              : form[`${keyPrefix}_consultation_strikeoff`] !== undefined
                ? `${keyPrefix}_consultation_strikeoff`
                : `${keyPrefix}_monthly_strikeoff`,
          v
        )}
        helper="Set 0 to hide the strikeoff display"
        data-testid={`field-${keyPrefix}-strikeoff`}
      />
    </div>
  );

  const Tab = ({ id, label, icon: Icon }) => (
    <button
      onClick={() => setTab(id)}
      className={`px-3 py-2 text-sm font-medium border-b-2 flex items-center gap-1.5 whitespace-nowrap shrink-0 ${tab === id ? 'border-primary text-primary' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
      data-testid={`tab-${id}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">
            {existing ? `Edit pricing — ${existing.country}` : 'Add country pricing'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl" data-testid="modal-close-btn">×</button>
        </div>

        <div className="px-6 pt-4">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Country</label>
              <select
                value={form.country}
                onChange={(e) => onCountryChange(e.target.value)}
                className="input-base"
                disabled={existing?.is_seeded}
                data-testid="field-country"
              >
                {Object.keys(COUNTRY_PRESETS).map((c) => <option key={c} value={c}>{c}</option>)}
                {existing && !COUNTRY_PRESETS[existing.country] && (
                  <option value={existing.country}>{existing.country}</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Currency</label>
              <input
                type="text"
                value={form.currency}
                disabled
                className="input-base bg-slate-50 text-slate-500"
                data-testid="field-currency"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Gateway</label>
              <input
                type="text"
                value={form.payment_gateway}
                disabled
                className="input-base bg-slate-50 text-slate-500 uppercase"
                data-testid="field-gateway"
              />
            </div>
          </div>
        </div>

        <div className="px-6 border-b border-slate-200 flex flex-wrap gap-1" data-testid="pricing-modal-tabs">
          <Tab id="trial" label="Trial" icon={Tag} />
          <Tab id="consult" label="Consultations" icon={Stethoscope} />
          <Tab id="elite" label="Elite (in-app)" icon={Crown} />
          <Tab id="website" label="Website Plans" icon={Crown} />
          <Tab id="soulai" label="SoulAI Coach" icon={Sparkles} />
          <Tab id="baby" label="Baby Tracker" icon={Baby} />
          <Tab id="tax" label="Tax & Status" icon={Receipt} />
          <Tab id="discount" label="Discounts" icon={Percent} />
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
          {tab === 'trial' && (
            <>
              <PriceRow keyPrefix="trial" label="Trial class" />
              <p className="text-xs text-slate-500 pt-2 border-t border-slate-200">
                Used by the website /trial landing page, doctor QR landing pages, and the App's trial-purchase flow.
                Garbhsanskar / per-course pricing is now in the <strong>Course Catalog</strong> section below.
              </p>
            </>
          )}

          {tab === 'consult' && (
            <>
              <PriceRow keyPrefix="gynae" label="Gynae consultation" />
              <PriceRow keyPrefix="nutritionist" label="Nutritionist consultation" />
              <p className="text-xs text-slate-500">
                Used by /api/public/consultation-pricing — read by both OPS calendar booking and App in-app booking.
              </p>
            </>
          )}

          {tab === 'elite' && (
            <>
              <PriceRow keyPrefix="elite" label="Elite Monthly (in-app upgrade)" />
              <p className="text-xs text-slate-500">
                Used by the App for the in-app upgrade flow. <strong>This is independent</strong> of the website monthly price below.
              </p>
            </>
          )}

          {tab === 'website' && (
            <>
              <Field
                label={`Website Monthly Price (${sym})`}
                value={form.default_monthly_price}
                onChange={(v) => set('default_monthly_price', v)}
                helper="Used by the website's monthly enrollment flow."
                data-testid="field-default-monthly"
              />
              <Field
                label={`Website "Till Delivery" — Starts from / month (${sym})`}
                value={form.till_delivery_starting_price}
                onChange={(v) => set('till_delivery_starting_price', v)}
                helper="Headline starting-from price for the Till-Delivery plan on the website. Fetched by the website frontend team via /api/pricing/public/full → website_plans.till_delivery_starting_from."
                data-testid="field-till-delivery"
              />
              <p className="text-xs text-slate-500">
                These two prices are <strong>independent</strong> of the in-app Elite price. Frontend website team auto-fetches from <code>/api/pricing/public/full</code>.
              </p>
            </>
          )}

          {tab === 'soulai' && (
            <div className="space-y-4" data-testid="soulai-tab">
              <p className="text-xs text-muted-foreground">Sold from the APP (self-serve) and via sales payment links. Monthly = one-time 30 days. Till-Delivery = one flat offer applied to the monthly price for every month up to EDD+3d.</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label={`Monthly price (${sym})`} value={form.soulai_coach_monthly_price} onChange={(v) => set('soulai_coach_monthly_price', v)} data-testid="field-soulai-monthly" />
                <Field label="Monthly strikeoff" value={form.soulai_coach_monthly_strikeoff} onChange={(v) => set('soulai_coach_monthly_strikeoff', v)} helper="0 hides strikeoff" data-testid="field-soulai-monthly-strikeoff" />
              </div>

              {(() => {
                const monthly = Number(form.soulai_coach_monthly_price) || 0;
                const mode = form.soulai_till_delivery_display_as || 'percent';
                const disc = Number(form.soulai_till_delivery_discount_pct) || 0;
                const price = Number(form.soulai_till_delivery_effective_price) || 0;
                const effPrice = mode === 'price' ? price : Math.round(monthly * (1 - disc / 100));
                const effDisc = monthly ? Math.round((1 - effPrice / monthly) * 1000) / 10 : 0;
                const setMode = (m) => set('soulai_till_delivery_display_as', m);
                return (
                  <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="soulai-td-offer">
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide">Till-Delivery starting offer</label>
                    <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
                      <button type="button" onClick={() => setMode('percent')} data-testid="td-mode-percent"
                        className={`px-3 py-1.5 ${mode === 'percent' ? 'bg-primary text-white' : 'bg-white text-slate-600'}`}>% off</button>
                      <button type="button" onClick={() => setMode('price')} data-testid="td-mode-price"
                        className={`px-3 py-1.5 ${mode === 'price' ? 'bg-primary text-white' : 'bg-white text-slate-600'}`}>{sym}/mo price</button>
                    </div>
                    {mode === 'percent' ? (
                      <Field label="Discount (%)" value={form.soulai_till_delivery_discount_pct}
                        onChange={(v) => set('soulai_till_delivery_discount_pct', v)}
                        helper='App shows "Till-Delivery plans starting at X% off".' data-testid="field-td-discount" />
                    ) : (
                      <Field label={`Starting price / month (${sym})`} value={form.soulai_till_delivery_effective_price}
                        onChange={(v) => set('soulai_till_delivery_effective_price', v)}
                        helper='App shows "Till-Delivery starting from ₹X/mo". Discount auto-calculated.' data-testid="field-td-price" />
                    )}
                    <p className="text-[11px] text-muted-foreground" data-testid="td-offer-preview">
                      App/webapp will show: <b>{mode === 'percent' ? `Till-Delivery plans starting at ${disc}% off` : `Till-Delivery starting from ${sym}${effPrice.toLocaleString()}/mo`}</b>
                      {' '}· effective {sym}{effPrice.toLocaleString()}/mo ({effDisc}% off {sym}{monthly.toLocaleString()}). Sent to LMS: discount_pct={effDisc}, monthly_ref_amount={monthly}, effective_monthly_amount={effPrice}, display_as="{mode}".
                    </p>
                  </div>
                );
              })()}
            </div>
          )}

          {tab === 'baby' && (
            <div className="space-y-4" data-testid="baby-tab">
              <p className="text-xs text-muted-foreground">Baby Tracker is independent of Elite/SoulAI. Monthly and yearly one-time purchases.</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label={`Monthly price (${sym})`} value={form.baby_tracker_monthly_price} onChange={(v) => set('baby_tracker_monthly_price', v)} data-testid="field-baby-monthly" />
                <Field label="Monthly strikeoff" value={form.baby_tracker_monthly_strikeoff} onChange={(v) => set('baby_tracker_monthly_strikeoff', v)} data-testid="field-baby-monthly-strikeoff" />
                <Field label={`Yearly price (${sym})`} value={form.baby_tracker_yearly_price} onChange={(v) => set('baby_tracker_yearly_price', v)} data-testid="field-baby-yearly" />
                <Field label="Yearly strikeoff" value={form.baby_tracker_yearly_strikeoff} onChange={(v) => set('baby_tracker_yearly_strikeoff', v)} data-testid="field-baby-yearly-strikeoff" />
              </div>
            </div>
          )}

          {tab === 'tax' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="GST %"
                  value={form.gst_percentage}
                  onChange={(v) => set('gst_percentage', v)}
                  data-testid="field-gst"
                />
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={!!form.prices_include_gst}
                      onChange={(e) => set('prices_include_gst', e.target.checked)}
                      data-testid="field-prices-include-gst"
                    />
                    Prices already include GST
                  </label>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={!!form.active_status}
                  onChange={(e) => set('active_status', e.target.checked)}
                  data-testid="field-active"
                />
                Active (toggle off to hide from public APIs)
              </label>
            </>
          )}

          {tab === 'discount' && (
            <DiscountsTab country={form.country} currency={form.currency} sym={sym} />
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" data-testid="modal-cancel-btn">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="modal-save-btn">
            <DollarSign className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;

// ─── Discount sub-tab inside Pricing modal ────────────────────────────────────
// Scoped to current country/currency. Uses /api/offers CRUD (same backend as the
// global /offers page — both views always stay in sync, no duplicate writes).

const APPLIES_TO_OPTIONS = [
  { key: 'trial', label: 'Trial Class' },
  { key: 'gynae_consult', label: 'Gynae Consultation' },
  { key: 'nutritionist_consult', label: 'Nutritionist Consultation' },
  { key: 'elite_monthly', label: 'Elite Monthly' },
  { key: 'soulai_coach', label: 'SoulAI Coach (APP)' },
  { key: 'baby_tracker', label: 'Baby Tracker (APP)' },
];

const blankOffer = {
  campaign_name: '',
  applies_to: ['elite_monthly'],
  discount_type: 'percentage',
  discount_percentage: 10,
  discount_amount_flat: null,
  coupon_code: '',
  show_coupon_on_website: false,
  start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  is_active: true,
  landing_page_slugs: [],
  max_redemptions: null,
  once_per_user: false,
  min_amount: null,
};

const DiscountsTab = ({ country, currency, sym }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view; obj = edit form
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/offers`, { withCredentials: true });
      setOffers((res.data || []).filter((o) => o.country === country && o.currency === currency));
    } catch {
      toast.error('Failed to load discounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [country, currency]);

  const handleDelete = async (offer) => {
    if (!window.confirm(`Delete discount "${offer.campaign_name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/offers/${offer.offer_id}`, { withCredentials: true });
      toast.success('Discount deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggle = async (offer) => {
    try {
      await axios.put(
        `${API_URL}/api/offers/${offer.offer_id}`,
        { is_active: !offer.is_active },
        { withCredentials: true }
      );
      toast.success(offer.is_active ? 'Discount deactivated' : 'Discount activated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Toggle failed');
    }
  };

  if (creating || editing) {
    return (
      <DiscountForm
        existing={editing}
        country={country}
        currency={currency}
        sym={sym}
        onCancel={() => { setEditing(null); setCreating(false); }}
        onSaved={() => { setEditing(null); setCreating(false); load(); }}
      />
    );
  }

  return (
    <div className="space-y-3" data-testid="discounts-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Promo codes &amp; discounts for <span className="font-medium text-slate-700">{country}</span> ({currency}).
          Read by website checkout, App, and LMS via <code className="text-[10px] bg-slate-100 px-1 rounded">/api/pricing/public/full</code>.
        </p>
        <button
          onClick={() => setCreating(true)}
          className="btn-primary flex items-center gap-1 text-xs px-3 py-1.5"
          data-testid="add-discount-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add Discount
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-500 text-sm">Loading...</div>
      ) : offers.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm border border-dashed border-slate-200 rounded-lg">
          No discounts configured for {country}. Click "Add Discount" to create one.
        </div>
      ) : (
        <div className="space-y-2">
          {offers.map((o) => {
            const today = new Date().toISOString().slice(0, 10);
            const expired = o.end_date < today;
            const upcoming = o.start_date > today;
            const live = o.is_active && !expired && !upcoming;
            const valueText = o.discount_type === 'flat'
              ? `${sym}${o.discount_amount_flat || 0} off`
              : `${o.discount_percentage || 0}% off`;
            return (
              <div
                key={o.offer_id}
                className="border border-slate-200 rounded-lg p-3 flex items-start justify-between gap-3 hover:bg-slate-50"
                data-testid={`discount-row-${o.offer_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-slate-900 text-sm">{o.campaign_name}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-medium">
                      {valueText}
                    </span>
                    {live && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">LIVE</span>}
                    {!o.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-medium">PAUSED</span>}
                    {expired && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 font-medium">EXPIRED</span>}
                    {upcoming && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">UPCOMING</span>}
                  </div>
                  <div className="text-xs text-slate-600 space-y-0.5">
                    {o.coupon_code && (
                      <div>
                        Code: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-800 font-mono">{o.coupon_code}</code>
                        {o.show_coupon_on_website && <span className="ml-2 text-emerald-700">• Visible on website</span>}
                      </div>
                    )}
                    <div>
                      Applies to:{' '}
                      {(o.applies_to || []).map((k) => APPLIES_TO_OPTIONS.find((x) => x.key === k)?.label || k).join(', ') || '—'}
                    </div>
                    <div>{o.start_date} → {o.end_date}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleToggle(o)}
                    className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-700 hover:bg-white"
                    data-testid={`toggle-discount-${o.offer_id}`}
                  >
                    {o.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => setEditing(o)}
                    className="p-1.5 text-slate-600 hover:bg-white rounded"
                    data-testid={`edit-discount-${o.offer_id}`}
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(o)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                    data-testid={`delete-discount-${o.offer_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DiscountForm = ({ existing, country, currency, sym, onCancel, onSaved }) => {
  const [form, setForm] = useState(existing ? { ...blankOffer, ...existing } : { ...blankOffer, country, currency });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const toggleApply = (key) => {
    const cur = form.applies_to || [];
    set('applies_to', cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]);
  };

  const save = async () => {
    if (!form.campaign_name?.trim()) { toast.error('Campaign name is required'); return; }
    if ((form.applies_to || []).length === 0) { toast.error('Pick at least one product'); return; }
    if (form.discount_type === 'percentage') {
      const p = Number(form.discount_percentage);
      if (!(p > 0 && p <= 100)) { toast.error('Percentage must be between 0 and 100'); return; }
    } else {
      const a = Number(form.discount_amount_flat);
      if (!(a > 0)) { toast.error('Flat discount amount must be greater than 0'); return; }
    }
    if (form.start_date > form.end_date) { toast.error('Start date must be before end date'); return; }

    setSaving(true);
    try {
      const payload = {
        campaign_name: form.campaign_name.trim(),
        country,
        currency,
        applies_to: form.applies_to,
        discount_type: form.discount_type,
        discount_percentage: form.discount_type === 'percentage' ? Number(form.discount_percentage) : null,
        discount_amount_flat: form.discount_type === 'flat' ? Number(form.discount_amount_flat) : null,
        coupon_code: form.coupon_code?.trim() || null,
        show_coupon_on_website: !!form.show_coupon_on_website,
        landing_page_slugs: form.landing_page_slugs || [],
        start_date: form.start_date,
        end_date: form.end_date,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        once_per_user: !!form.once_per_user,
        min_amount: form.min_amount ? Number(form.min_amount) : null,
      };
      if (existing) {
        await axios.put(`${API_URL}/api/offers/${existing.offer_id}`, { ...payload, is_active: form.is_active }, { withCredentials: true });
        toast.success('Discount updated');
      } else {
        await axios.post(`${API_URL}/api/offers`, payload, { withCredentials: true });
        toast.success('Discount created');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="discount-form">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">
          {existing ? 'Edit discount' : 'Add discount'} — {country} ({currency})
        </h3>
        <button onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-800" data-testid="discount-form-back">← Back to list</button>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">Campaign name *</label>
        <input
          type="text"
          value={form.campaign_name || ''}
          onChange={(e) => set('campaign_name', e.target.value)}
          placeholder="e.g., Diwali 2026"
          className="input-base"
          data-testid="discount-field-name"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1.5">Applies to *</label>
        <div className="grid grid-cols-2 gap-2">
          {APPLIES_TO_OPTIONS.map((opt) => (
            <label key={opt.key} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={(form.applies_to || []).includes(opt.key)}
                onChange={() => toggleApply(opt.key)}
                data-testid={`discount-applies-${opt.key}`}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1.5">Discount type *</label>
        <div className="flex gap-4 mb-2">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              checked={form.discount_type === 'percentage'}
              onChange={() => set('discount_type', 'percentage')}
              data-testid="discount-type-percentage"
            />
            Percentage off (%)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              checked={form.discount_type === 'flat'}
              onChange={() => set('discount_type', 'flat')}
              data-testid="discount-type-flat"
            />
            Flat amount off ({sym})
          </label>
        </div>
        {form.discount_type === 'percentage' ? (
          <input
            type="number"
            min="1" max="100" step="1"
            value={form.discount_percentage ?? ''}
            onChange={(e) => set('discount_percentage', e.target.value)}
            placeholder="10"
            className="input-base"
            data-testid="discount-value-percentage"
          />
        ) : (
          <input
            type="number"
            min="1" step="1"
            value={form.discount_amount_flat ?? ''}
            onChange={(e) => set('discount_amount_flat', e.target.value)}
            placeholder={`500 (= ${sym}500 off)`}
            className="input-base"
            data-testid="discount-value-flat"
          />
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Coupon code</label>
          <input
            type="text"
            value={form.coupon_code || ''}
            onChange={(e) => set('coupon_code', e.target.value.toUpperCase())}
            placeholder="e.g., DIWALI10"
            className="input-base font-mono"
            data-testid="discount-field-code"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={!!form.show_coupon_on_website}
              onChange={(e) => set('show_coupon_on_website', e.target.checked)}
              data-testid="discount-show-on-website"
            />
            Show on website checkout
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Start date *</label>
          <input
            type="date"
            value={form.start_date || ''}
            onChange={(e) => set('start_date', e.target.value)}
            className="input-base"
            data-testid="discount-field-start"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">End date *</label>
          <input
            type="date"
            value={form.end_date || ''}
            onChange={(e) => set('end_date', e.target.value)}
            className="input-base"
            data-testid="discount-field-end"
          />
        </div>
      </div>

      <div className="rounded-lg border border-border p-3 space-y-3" data-testid="coupon-limits">
        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Coupon limits (enforced by LMS validate/redeem)</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Max redemptions</label>
            <input type="number" className="input-base" value={form.max_redemptions ?? ''} onChange={(e) => set('max_redemptions', e.target.value)} placeholder="unlimited" data-testid="discount-field-max-redemptions" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Min order amount ({sym})</label>
            <input type="number" className="input-base" value={form.min_amount ?? ''} onChange={(e) => set('min_amount', e.target.value)} placeholder="none" data-testid="discount-field-min-amount" />
          </div>
          <label className="flex items-end gap-2 text-sm text-slate-700 pb-2">
            <input type="checkbox" checked={!!form.once_per_user} onChange={(e) => set('once_per_user', e.target.checked)} data-testid="discount-field-once-per-user" />
            Once per user
          </label>
        </div>
        {existing && <p className="text-[11px] text-muted-foreground">Redeemed so far: <b>{existing.redemption_count || 0}</b>{existing.max_redemptions ? ` / ${existing.max_redemptions}` : ''}</p>}
      </div>

      {existing && (
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={!!form.is_active}
            onChange={(e) => set('is_active', e.target.checked)}
            data-testid="discount-field-active"
          />
          Active
        </label>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="btn-secondary" data-testid="discount-cancel-btn">Discard changes</button>
        <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="discount-save-btn">
          <Percent className="w-4 h-4" /> {saving ? 'Saving...' : existing ? 'Update Discount' : 'Create Discount'}
        </button>
      </div>
    </div>
  );
};



// ════════════════════════════════════════════════════════════════════════
// V29 — Course Catalog (per-course product table)
// Lives inside the Pricing tab. Two-region pricing: India (INR) + Rest of World (USD).
// LMS Learn-section consumes via GET /api/catalog/public/{sku}?region=IN|ROW
// ════════════════════════════════════════════════════════════════════════

const COURSE_TYPES = [
  { value: 'prenatal',     label: 'Prenatal' },
  { value: 'postnatal',    label: 'Postnatal' },
  { value: 'ttc',          label: 'TTC' },
  { value: 'garbhsanskar', label: 'Garbhsanskar' },
  { value: 'other',        label: 'Other' },
];

const blankCourse = {
  sku: '',
  display_name: '',
  description: '',
  course_type: 'other',
  pricing_inr: { paid_trial_price: 0, paid_trial_strikeoff: 0, full_course_price: 0, full_course_strikeoff: 0 },
  pricing_row: { paid_trial_price: 0, paid_trial_strikeoff: 0, full_course_price: 0, full_course_strikeoff: 0 },
  has_paid_trial: false,
  has_full_course: true,
  is_active: true,
};

const CatalogSection = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/catalog?include_inactive=${includeArchived}`,
        { withCredentials: true },
      );
      setCourses(res.data || []);
    } catch (e) {
      toast.error('Failed to load course catalog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [includeArchived]);

  const handleAdd = () => { setEditing(null); setShowModal(true); };
  const handleEdit = (course) => { setEditing(course); setShowModal(true); };

  const handleArchive = async (course) => {
    if (!window.confirm(`Archive "${course.display_name}" (${course.sku})? It will be hidden from the LMS Learn section.`)) return;
    try {
      await axios.delete(`${API_URL}/api/catalog/${course.sku}`, { withCredentials: true });
      toast.success('Course archived');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archive failed');
    }
  };

  return (
    <div className="mt-10 pt-8 border-t border-slate-200" data-testid="catalog-section">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-violet-600" />
            <h2 className="text-xl font-bold text-slate-900">Course Catalog</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            Per-course pricing (India / Rest of World). Surfaced in the LMS Learn section. Not on the public website.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="catalog-include-archived" />
            Show archived
          </label>
          <button onClick={handleAdd} className="btn-primary flex items-center gap-2" data-testid="catalog-add-course-btn">
            <Plus className="w-4 h-4" /> Add Course
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-500 text-sm">Loading catalog...</div>
      ) : courses.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
          <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No courses yet. Click "Add Course" to paste a new LMS-generated SKU.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm" data-testid="catalog-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wide text-slate-600">
                <th className="px-4 py-3 font-semibold">Course</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">India (INR)</th>
                <th className="px-4 py-3 font-semibold">Rest of World (USD)</th>
                <th className="px-4 py-3 font-semibold">Tiers</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {courses.map((c) => (
                <CatalogRow key={c.sku} course={c} onEdit={() => handleEdit(c)} onArchive={() => handleArchive(c)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <CatalogModal existing={editing} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />
      )}
    </div>
  );
};

const CatalogRow = ({ course, onEdit, onArchive }) => {
  const inr = course.pricing_inr || {};
  const row = course.pricing_row || {};
  const fmt = (v) => Number(v || 0).toLocaleString();
  return (
    <tr className={`hover:bg-slate-50 ${!course.is_active ? 'opacity-50' : ''}`} data-testid={`catalog-row-${course.sku}`}>
      <td className="px-4 py-3">
        <div className="font-medium text-slate-900">{course.display_name}</div>
        <div className="text-xs text-slate-500 font-mono">{course.sku}</div>
        {!course.is_active && (
          <span className="inline-block text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-medium mt-1">ARCHIVED</span>
        )}
      </td>
      <td className="px-4 py-3 text-slate-600 capitalize">{course.course_type}</td>
      <td className="px-4 py-3 text-xs">
        {course.has_paid_trial && (
          <div>Trial: {fmt(inr.paid_trial_price)}{inr.paid_trial_strikeoff > 0 && <span className="text-slate-400 line-through ml-1">{fmt(inr.paid_trial_strikeoff)}</span>}</div>
        )}
        {course.has_full_course && (
          <div>Full: {fmt(inr.full_course_price)}{inr.full_course_strikeoff > 0 && <span className="text-slate-400 line-through ml-1">{fmt(inr.full_course_strikeoff)}</span>}</div>
        )}
      </td>
      <td className="px-4 py-3 text-xs">
        {course.has_paid_trial && (
          <div>Trial: {fmt(row.paid_trial_price)}{row.paid_trial_strikeoff > 0 && <span className="text-slate-400 line-through ml-1">{fmt(row.paid_trial_strikeoff)}</span>}</div>
        )}
        {course.has_full_course && (
          <div>Full: {fmt(row.full_course_price)}{row.full_course_strikeoff > 0 && <span className="text-slate-400 line-through ml-1">{fmt(row.full_course_strikeoff)}</span>}</div>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-slate-600">
        {course.has_paid_trial && <span className="inline-block bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded mr-1 mb-0.5">Paid Trial</span>}
        {course.has_full_course && <span className="inline-block bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">Full Course</span>}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex justify-end gap-1">
          <button onClick={onEdit} className="p-1.5 text-slate-600 hover:bg-slate-100 rounded" title="Edit" data-testid={`catalog-edit-${course.sku}`}>
            <Edit2 className="w-4 h-4" />
          </button>
          {course.is_active && (
            <button onClick={onArchive} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="Archive" data-testid={`catalog-archive-${course.sku}`}>
              <Archive className="w-4 h-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

const CatalogModal = ({ existing, onClose, onSaved }) => {
  const [form, setForm] = useState(existing ? {
    ...blankCourse,
    ...existing,
    pricing_inr: { ...blankCourse.pricing_inr, ...(existing.pricing_inr || {}) },
    pricing_row: { ...blankCourse.pricing_row, ...(existing.pricing_row || {}) },
  } : blankCourse);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setRegion = (region, k, v) => {
    setForm({ ...form, [region]: { ...form[region], [k]: Number(v) || 0 } });
  };

  const validate = () => {
    if (!form.sku.trim()) return 'SKU is required (paste from LMS).';
    if (!form.display_name.trim()) return 'Display name is required.';
    if (!form.has_paid_trial && !form.has_full_course) return 'At least one tier (Paid Trial or Full Course) must be enabled.';
    for (const region of ['pricing_inr', 'pricing_row']) {
      const r = form[region];
      const regionLabel = region === 'pricing_inr' ? 'India' : 'Rest of World';
      if (form.has_paid_trial && r.paid_trial_strikeoff > 0 && r.paid_trial_strikeoff < r.paid_trial_price) {
        return `${regionLabel}: Paid trial strikeoff (${r.paid_trial_strikeoff}) must be >= price (${r.paid_trial_price}). Set 0 to hide.`;
      }
      if (form.has_full_course && r.full_course_strikeoff > 0 && r.full_course_strikeoff < r.full_course_price) {
        return `${regionLabel}: Full course strikeoff (${r.full_course_strikeoff}) must be >= price (${r.full_course_price}). Set 0 to hide.`;
      }
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      if (existing) {
        const { sku, ...updates } = form;
        await axios.put(`${API_URL}/api/catalog/${existing.sku}`, updates, { withCredentials: true });
        toast.success('Course updated');
      } else {
        await axios.post(`${API_URL}/api/catalog`, form, { withCredentials: true });
        toast.success('Course added');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const PriceCols = ({ region, regionLabel, sym }) => {
    const r = form[region];
    return (
      <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
        <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">{regionLabel} ({sym})</h4>
        {form.has_paid_trial && (
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="block text-[11px] font-medium text-slate-700 mb-0.5">Paid Trial Price</label>
              <input type="number" value={r.paid_trial_price ?? 0} onChange={(e) => setRegion(region, 'paid_trial_price', e.target.value)} className="input-base text-sm" data-testid={`catalog-${region}-paid-trial-price`} />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-slate-700 mb-0.5">Paid Trial Strikeoff</label>
              <input type="number" value={r.paid_trial_strikeoff ?? 0} onChange={(e) => setRegion(region, 'paid_trial_strikeoff', e.target.value)} className="input-base text-sm" data-testid={`catalog-${region}-paid-trial-strikeoff`} />
            </div>
          </div>
        )}
        {form.has_full_course && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] font-medium text-slate-700 mb-0.5">Full Course Price</label>
              <input type="number" value={r.full_course_price ?? 0} onChange={(e) => setRegion(region, 'full_course_price', e.target.value)} className="input-base text-sm" data-testid={`catalog-${region}-full-course-price`} />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-slate-700 mb-0.5">Full Course Strikeoff</label>
              <input type="number" value={r.full_course_strikeoff ?? 0} onChange={(e) => setRegion(region, 'full_course_strikeoff', e.target.value)} className="input-base text-sm" data-testid={`catalog-${region}-full-course-strikeoff`} />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="catalog-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">
            {existing ? `Edit course - ${existing.display_name}` : 'Add new course'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl" data-testid="catalog-modal-close">×</button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">SKU (from LMS)</label>
              <input type="text" value={form.sku} onChange={(e) => set('sku', e.target.value.toLowerCase().trim())} className="input-base font-mono" placeholder="ss_course_a8f3c2d1" disabled={!!existing} data-testid="catalog-field-sku" />
              <p className="text-[11px] text-slate-500 mt-0.5">
                {existing ? 'SKU cannot be changed after creation.' : 'Paste the auto-generated SKU from LMS course catalog.'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Display Name</label>
              <input type="text" value={form.display_name} onChange={(e) => set('display_name', e.target.value)} className="input-base" placeholder="Prenatal Yoga Foundations" data-testid="catalog-field-display-name" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Course Type</label>
              <select value={form.course_type} onChange={(e) => set('course_type', e.target.value)} className="input-base" data-testid="catalog-field-course-type">
                {COURSE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!form.has_paid_trial} onChange={(e) => set('has_paid_trial', e.target.checked)} data-testid="catalog-field-has-paid-trial" />
                Has Paid Trial
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!form.has_full_course} onChange={(e) => set('has_full_course', e.target.checked)} data-testid="catalog-field-has-full-course" />
                Has Full Course
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Description (optional)</label>
            <textarea value={form.description || ''} onChange={(e) => set('description', e.target.value)} className="input-base" rows={2} placeholder="Sales-internal notes for this course" data-testid="catalog-field-description" />
          </div>

          <div className="space-y-3 pt-2 border-t border-slate-200">
            <PriceCols region="pricing_inr" regionLabel="India" sym="INR" />
            <PriceCols region="pricing_row" regionLabel="Rest of World" sym="USD" />
          </div>

          {existing && (
            <label className="flex items-center gap-2 text-sm text-slate-700 pt-2 border-t border-slate-200">
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => set('is_active', e.target.checked)} data-testid="catalog-field-is-active" />
              Active (visible in LMS Learn section)
            </label>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" data-testid="catalog-modal-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="catalog-modal-save">
            <BookOpen className="w-4 h-4" /> {saving ? 'Saving...' : existing ? 'Update Course' : 'Add Course'}
          </button>
        </div>
      </div>
    </div>
  );
};
